import axios, { AxiosError } from 'axios';
import { flow, Instance, types } from 'mobx-state-tree';

import endpoints from '@configs/endpoints';
import { baseDigitalPaymentsUrl } from '@configs/variables';
import { Partner } from '@constants/paymentLink';
import { IPayeeInfo, IPayerAddress } from '@containers/checkout/payment-form/types';
import Common from '@store/common';
import { generateDeviceId } from '@utils/deviceId';
import getCardInfo from '@utils/getCardInfo';
import { getCurrentUrl, getProcessingUrl } from '@utils/getUrlParam';

export interface FormPayload extends IPayeeInfo, IPayerAddress {
  cardNumber: string;
  cvv2: string;
  expiration: string;
  holderName: string;
}

export interface PayPayload extends FormPayload {
  paymentLinkId: string;
}

export interface DigitalPaymentsError extends AxiosError {
  statusCode: string;
  error: string;
  message: string;
  timestamp: string;
}

const Fiserv = types
  .compose(
    Common,
    types.model({
      token: types.optional(types.string, ''),
      paymentIntentId: types.optional(types.string, ''),
      cardToken: types.optional(types.string, ''),
      iframeCode: types.optional(types.string, ''),
      acsURL: types.optional(types.string, ''),
      cReq: types.optional(types.string, ''),
      transactionId: types.optional(types.string, ''),
      approvedTransaction: types.optional(types.boolean, false),
      flags: types.optional(
        types.model({
          isSendingPayment: false,
        }),
        {},
      ),
    }),
  )
  .volatile(() => ({
    deviceIdRecord: '',
  }))
  .views((self) => ({
    get isProcessing() {
      return self.flags.isSendingPayment;
    },
    get deviceId() {
      if (self.deviceIdRecord) return self.deviceIdRecord;

      const deviceId = `${generateDeviceId()}`;
      if (deviceId) {
        self.updateField('deviceIdRecord', deviceId);
      }

      return deviceId;
    },
  }))
  .actions((self) => ({
    setIsSendingPayment: (value: boolean) => {
      self.flags.isSendingPayment = value;
    },
    restoreValues: () => {
      self.flags.isSendingPayment = false;
      self.iframeCode = '';
      self.acsURL = '';
      self.cReq = '';
      self.transactionId = '';
    },
    getCommonDetails: (parameters: PayPayload) => {
      return {
        contactDetails: {
          email: parameters.payeeEmail.trim(),
          firstName: parameters.payeeFirstName,
          lastName: parameters.payeeLastName,
          phoneNumber: parameters.payeePhone.replaceAll(' ', ''),
        },
        billingDetails: {
          name: parameters.holderName,
          phone: parameters.payeePhone.replaceAll(' ', ''),
          address: parameters.payerAddress,
          city: parameters.payerCity,
          region: parameters.payerState,
          country: parameters.payerCountry,
          zipCode: parameters.payerZipCode,
        },
      };
    },
  }))
  .actions((self) => ({
    login: flow(function* () {
      self.token = '';
      const { platformId, apiKey, workspaceId, walletId } =
        self.globalStore.links.paymentConfig.paymentMethods.card;
      const payload = { platformId, apiKey, workspaceId, walletId };

      try {
        const { data } = yield axios.post(endpoints.loginDigitalPayments, payload, {
          baseURL: baseDigitalPaymentsUrl,
        });
        self.token = data.data.token;
        return data;
      } catch (error) {
        const { response, message } = error as DigitalPaymentsError;
        throw new Error(response?.data.message || message);
      }
    }),
    paymentIntent: flow(function* (parameters: PayPayload) {
      self.paymentIntentId = '';
      const { currency, total, title, linkNumberFormatted } = self.globalStore.links.paymentLink;
      const { businessName } = self.globalStore.links.accountInfo;
      const payload = {
        currency: currency,
        amount: +total,
        concept: title,
        reference: linkNumberFormatted,
        payer: {
          name: `${parameters.payeeFirstName} ${parameters.payeeLastName}`,
          email: parameters.payeeEmail.trim(),
          phoneNumber: parameters.payeePhone.replaceAll(' ', ''),
        },
        payee: {
          alias: businessName,
          email: 'some@email.example',
        },
        createdBy: 'payee',
      };

      try {
        const { data } = yield axios.post(endpoints.paymentIntents, payload, {
          baseURL: baseDigitalPaymentsUrl,
          headers: { Authorization: self.token },
        });

        self.paymentIntentId = data.data.uuid;

        return data;
      } catch (error) {
        const { response, message } = error as DigitalPaymentsError;
        throw new Error(response?.data.message || message);
      }
    }),
    cardPayment: flow(function* (parameters: PayPayload) {
      const paymentLinkInfo = {
        token: self.cardToken,
        payeeFirstName: parameters.payeeFirstName,
        payeeLastName: parameters.payeeLastName,
        payeeEmail: parameters.payeeEmail.trim(),
        payeePhone: parameters.payeePhone.replaceAll(' ', ''),
        paymentLinkId: parameters.paymentLinkId,
        partner: Partner.FISERV,
        externalRefId: self.paymentIntentId,
      };

      const { bin, last4 } = getCardInfo(parameters.cardNumber);
      const cardInfo = {
        holderName: parameters.holderName,
        bin,
        last4,
      };

      const payload = {
        ...paymentLinkInfo,
        deviceId: self.deviceId,
        confirmationUrl: getCurrentUrl(parameters.paymentLinkId),
        cardInfo,
      };

      try {
        const { data } = yield axios.post(endpoints.processCardPayment, payload);
        return data;
      } catch (error) {
        const { response } = error as AxiosError;
        throw new Error(response?.data.msg || (error as { msg: string }).msg);
      }
    }),
    generateCardToken: flow(function* (parameters: PayPayload) {
      self.cardToken = '';
      const [month, year] = parameters.expiration.split('/');
      try {
        const payload = {
          name: parameters.holderName,
          number: parameters.cardNumber.replaceAll(' ', ''),
          month,
          year,
          cvv: parameters.cvv2,
        };

        const { data } = yield axios.post(endpoints.cardToken, payload, {
          baseURL: baseDigitalPaymentsUrl,
          headers: { Authorization: self.token },
        });

        self.cardToken = data.data.token;

        return data;
      } catch (error) {
        const { response, message } = error as DigitalPaymentsError;
        throw new Error(response?.data.message || message);
      }
    }),
    paymentsSecure: flow(function* (parameters: PayPayload) {
      const payload = {
        token: self.cardToken,
        paymentIntentId: self.paymentIntentId,
        paymentMethod: 'card',
        type: 'charge',
        redirectUrl: getProcessingUrl(),
        ...self.getCommonDetails(parameters),
      };
      try {
        const { data } = yield axios.post(endpoints.paymentsSecure, payload, {
          baseURL: baseDigitalPaymentsUrl,
          headers: { Authorization: self.token },
        });

        const { authData = '' } = data?.data;
        if (authData) {
          // with validation
          self.iframeCode = authData.data.authResponseData.methodForm;
          self.transactionId = authData.data.ipgTransactionId;

          // challenge 3DSecure
          self.acsURL = authData.data.authResponseData.acsURL;
          self.cReq = authData.data.authResponseData.cReq;
        }
        return data;
      } catch (error) {
        const { response, message } = error as DigitalPaymentsError;
        throw new Error(response?.data.message || message);
      }
    }),
    paymentsSecureContinue: flow(function* (parameters: PayPayload) {
      const payload = {
        paymentIntentId: self.paymentIntentId,
        paymentMethod: 'card',
        ...self.getCommonDetails(parameters),
      };
      try {
        const { data } = yield axios.post(endpoints.paymentsSecureContinue, payload, {
          baseURL: baseDigitalPaymentsUrl,
          headers: { Authorization: self.token },
        });

        const { authData = '' } = data?.data;
        if (authData) {
          self.acsURL = authData.data.authResponseData.acsURL;
          self.cReq = authData.data.authResponseData.cReq;
        }
        return data;
      } catch (error) {
        const { response, message } = error as DigitalPaymentsError;
        throw new Error(response?.data.message || message);
      }
    }),
    accessControlServer: () => {
      const form = document.createElement('form');
      form.method = 'post';
      form.action = self.acsURL;

      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = 'creq';
      hiddenField.value = self.cReq;

      form.appendChild(hiddenField);
      return form;
    },
    paymentIntents: flow(function* () {
      try {
        const { data } = yield axios.get(endpoints.paymentIntent(self.paymentIntentId), {
          baseURL: baseDigitalPaymentsUrl,
          headers: { Authorization: self.token },
        });

        return data;
      } catch (error) {
        const { response } = error as AxiosError;
        throw new Error(response?.data.message);
      }
    }),
    confirmPayment: flow(function* (parameters: PayPayload) {
      const payload = {
        paymentIntentId: self.paymentIntentId,
        token: self.cardToken,
        paymentMethod: 'card',
        type: 'charge',
        ...self.getCommonDetails(parameters),
      };
      try {
        const { data } = yield axios.post(endpoints.payments, payload, {
          baseURL: baseDigitalPaymentsUrl,
          headers: { Authorization: self.token },
        });
        return data;
      } catch (error) {
        const { response, message } = error as DigitalPaymentsError;
        throw new Error(response?.data.message || message);
      }
    }),
    updatePayment: flow(function* (success: boolean) {
      const payload = {
        paymentOrderId: self.paymentIntentId,
        paymentMethod: 'CARD_PAYMENT',
        status: success ? 'success' : 'error',
      };

      try {
        const { data } = yield axios.post(endpoints.updatePayment, payload);
        return data;
      } catch (error) {
        const { response } = error as AxiosError;
        throw new Error(response?.data.msg || (error as { msg: string }).msg);
      }
    }),
  }))
  .actions((self) => ({
    pay: flow(function* (parameters: PayPayload) {
      self.flags.isSendingPayment = true;
      try {
        // step 1: do login through DP /auth endpoint
        yield self.login();

        // step 2: call to DP /payment-intents
        yield self.paymentIntent(parameters);

        // step 3: call to DP /card/tokens
        yield self.generateCardToken(parameters);

        // step 4: call to Oyster /cards/payments to send paymentIntentId
        yield self.cardPayment(parameters);

        // step 5: call to DP /payments/secure
        const data = yield self.paymentsSecure(parameters);

        if (!self.iframeCode) {
          if (!self.acsURL) {
            // if there is no iframeCode neither acsURL means transaction was approved (Frictionless whithout validation)
            if (data?.data?.status === 'success') {
              self.approvedTransaction = true;
            } else {
              // failed transaction
              yield self.updatePayment(false);
              throw new Error();
            }
          } // else Challenge 3DSecure without validation
        } // else could be Frictionless with validation or Challenge 3DSecure with validation
      } catch (error) {
        const { message } = error as { message: string };
        self.restoreValues();
        throw new Error(message);
      } finally {
        self.flags.isSendingPayment = false;
      }
    }),
    payContinue: flow(function* (parameters: PayPayload) {
      self.flags.isSendingPayment = true;
      try {
        let success = false;
        // step 6: call to DP /payments/secure-continue
        if (!self.acsURL) {
          // could be Frictionless with validation or Challenge 3DSecure with validation or Challenge 3DSecure without validation
          const data = yield self.paymentsSecureContinue(parameters);
          success = data?.data?.status === 'success';
        }

        // step 7: call to POST acsURL (accessControlServer)
        if (self.acsURL) {
          // could be Challenge 3DSecure with validation or Challenge 3DSecure without validation
          const form = self.accessControlServer();
          return form;
        }

        if (success) {
          // finish Frictionless with validation
          self.approvedTransaction = true;
        } else {
          // failed transaction
          yield self.updatePayment(false);
          throw new Error();
        }
      } catch (error) {
        const { message } = error as { message: string };
        self.restoreValues();
        throw new Error(message);
      } finally {
        self.flags.isSendingPayment = false;
      }
    }),
    finishProcess: flow(function* (parameters: PayPayload) {
      self.flags.isSendingPayment = true;
      try {
        let success = self.approvedTransaction;
        if (!success) {
          // step 8: call to DP /payments
          const data = yield self.confirmPayment(parameters);

          if (data?.data?.status === 'success') {
            success = true;
          }
        }

        // step 9: call to Oyster /payment-links/update-payment
        yield self.updatePayment(success);
        return success;
      } catch (error) {
        const { message } = error as { message: string };
        throw new Error(message);
      } finally {
        self.restoreValues();
      }
    }),
  }));

export interface IFiservStore extends Instance<typeof Fiserv> {}
export default Fiserv;
